import React, { useMemo, useRef, useState, useEffect } from 'react'
import { WingsEyebrow, WingsSearchBar, WingsPagination } from '../../global-styles/wingspans-ui/components'
import './FilterableCardGrid.css'

/**
 * Build lowercase searchable text for an item.
 *
 * @param {Object} item
 * @param {string[]} searchFields
 * @param {(item: Object) => string|string[]|null|undefined} getSearchValues
 * @returns {string}
 */
const getSearchText = (item, searchFields, getSearchValues) => {
  if (typeof getSearchValues === 'function') {
    const values = getSearchValues(item)
    const arrayValues = Array.isArray(values) ? values : [values]
    return arrayValues.filter(Boolean).join(' ').toLowerCase()
  }

  if (!searchFields.length) {
    return ''
  }

  return searchFields
    .map((field) => item?.[field])
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/**
 * Reusable card grid with standardized search, newest/saved toggle, and pagination.
 *
 * @param {Object} props
 * @param {Object[]} props.items
 * @param {(item: Object) => React.ReactNode} props.renderItemCard
 * @param {(item: Object, idx: number) => string|number} [props.getItemKey]
 * @param {React.ReactNode} props.heading
 * @param {string} [props.headingColor]
 * @param {React.ReactNode} props.sortByLabel
 * @param {React.ReactNode} props.newestLabel
 * @param {React.ReactNode} props.savedLabel
 * @param {string} props.searchPlaceholder
 * @param {number} [props.itemsPerPage]
 * @param {string[]} [props.searchFields]
 * @param {(item: Object) => string|string[]|null|undefined} [props.getSearchValues]
 * @param {(item: Object) => boolean} [props.savedFilter]
 * @param {boolean} [props.initialShowSaved]
 * @param {boolean} [props.showSortToggle]
 * @param {boolean} [props.showSearch]
 * @param {React.ReactNode} [props.emptyState]
 * @param {React.RefObject<HTMLElement>} [props.scrollRef]
 * @param {string} [props.sectionClassName]
 * @param {string} [props.cardsContainerClassName]
 */
const FilterableCardGrid = ({
  items = [],
  renderItemCard,
  getItemKey = (item, idx) => item?.id ?? item?.slug ?? idx,
  heading,
  headingColor = 'teal',
  sortByLabel,
  newestLabel,
  savedLabel,
  searchPlaceholder,
  itemsPerPage = 3,
  searchFields = [],
  getSearchValues,
  savedFilter,
  initialShowSaved = false,
  showSortToggle = true,
  showSearch = true,
  emptyState = null,
  scrollRef,
  sectionClassName = '',
  cardsContainerClassName = 'w-full w-default-cards-container',
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showSaved, setShowSaved] = useState(initialShowSaved)
  const [currentPage, setCurrentPage] = useState(1)
  const localSectionRef = useRef(null)

  const normalizedItems = useMemo(() => (Array.isArray(items) ? items : []), [items])
  const canFilterSaved = showSortToggle && typeof savedFilter === 'function'
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()

  const filteredItems = useMemo(() => {
    const savedFilteredItems = canFilterSaved && showSaved ? normalizedItems.filter(savedFilter) : normalizedItems

    if (!normalizedSearchQuery) {
      return savedFilteredItems
    }

    return savedFilteredItems.filter((item) => getSearchText(item, searchFields, getSearchValues).includes(normalizedSearchQuery))
  }, [canFilterSaved, getSearchValues, normalizedItems, normalizedSearchQuery, savedFilter, searchFields, showSaved])

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)
  const activePage = Math.min(currentPage, totalPages || 1)

  const pagedItems = useMemo(() => {
    const startIndex = (activePage - 1) * itemsPerPage
    const endIndex = activePage * itemsPerPage
    return filteredItems.slice(startIndex, endIndex)
  }, [activePage, filteredItems, itemsPerPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [showSaved, searchQuery])

  const resolvedScrollRef = scrollRef ?? localSectionRef

  return (
    <section ref={resolvedScrollRef} className={`filterable-card-grid ${sectionClassName}`.trim()}>
      <div className="filterable-card-grid-header">
        <div className="filterable-card-grid-header-sort">
          {canFilterSaved && (
            <ul className="filterable-card-grid-sort-list" aria-label="Sort options">
              {/* <li className="filterable-card-grid-sort-list-item filterable-card-grid-sort-list-item-sort">{sortByLabel}</li> */}
              <li className="filterable-card-grid-sort-list-item filterable-card-grid-sort-list-item-newest">
                <button
                  type="button"
                  className={`filterable-card-grid-sort-link ${showSaved ? '' : 'active'}`.trim()}
                  aria-pressed={!showSaved}
                  onClick={() => setShowSaved(false)}
                >
                  {newestLabel}
                </button>
              </li>
              <li className="filterable-card-grid-sort-list-item filterable-card-grid-sort-list-item-saved">
                <button
                  type="button"
                  className={`filterable-card-grid-sort-link ${showSaved ? 'active' : ''}`.trim()}
                  aria-pressed={showSaved}
                  onClick={() => setShowSaved(true)}
                >
                  {savedLabel}
                </button>
              </li>
            </ul>
          )}
        </div>

        <div className="filterable-card-grid-header-title">
          <WingsEyebrow color={headingColor}>{heading}</WingsEyebrow>
        </div>

        <div className="filterable-card-grid-header-search">
          {showSearch && (
            <WingsSearchBar
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={setSearchQuery}
            />
          )}
        </div>
      </div>

      {pagedItems.length > 0 ? (
        <div className={cardsContainerClassName}>
          {pagedItems.map((item, idx) => (
            <React.Fragment key={getItemKey(item, idx)}>{renderItemCard(item)}</React.Fragment>
          ))}
        </div>
      ) : (
        emptyState
      )}

      <WingsPagination currentPage={activePage} totalPages={totalPages} onPageChange={setCurrentPage} scrollRef={resolvedScrollRef} />
    </section>
  )
}

export default FilterableCardGrid
